import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/api-client';

export interface TransferStatusData {
  jobId: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  progress: number;
  data: {
    preCommit: {
      fromAddress: string;
      toAddress: string;
      amount: string;
      fromChain: string;
      toChain: string;
      transferId: string;
      userId: string;
    };
    validatedAt: string;
  };
  createdAt: string;
  processedOn: string;
  finishedOn: string | null;
  attempts: number;
  result?: {
    success: boolean;
    requestId: string;
    txHash: string;
    intentId: string;
    status: string;
  };
  error?: string;
}

interface UseTransferStatusOptions {
  enabled: boolean;
  onStatusChange?: (status: TransferStatusData) => void;
}

export function useTransferStatus(
  jobId: string | null,
  { enabled, onStatusChange }: UseTransferStatusOptions,
) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['transfer-status', jobId],
    queryFn: async () => {
      if (!jobId) throw new Error(t('errors.noJobIdProvided'));
      const response = await apiClient<TransferStatusData>(
        `/api/v2/transfers/status/${jobId}`,
      );
      return response;
    },
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling when completed or failed
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false;
      }
      // Poll every 2 seconds while pending or active
      return 1000;
    },
    retry: false,
  });

  // Call the callback when status data changes
  useEffect(() => {
    if (query.data && onStatusChange) {
      onStatusChange(query.data);
    }
  }, [query.data, onStatusChange]);

  return query;
}
